import { Vector2 } from 'three';
import _ from 'underscore';

export enum MouseEventType {
  MouseMove,
  MouseDown,
  MouseUp,
  MouseWheel,
}

export enum MouseButtonType {
  LeftButton,
  MiddleButton,
  RightButton,
}

export interface TouchInputData {
  touches: {
    position: Vector2;
    client: Vector2;
    delta: Vector2;
    identifier: number;
  }[];
  changedTouches: {
    position: Vector2;
    client: Vector2;
  }[];
  center?: {
    position: Vector2;
    delta: Vector2;
  };
  touchLength?: {
    length: number;
    delta: number;
  };
}

export interface MouseInputData {
  type: MouseEventType;
  pointer: {
    client: Vector2;
    position: Vector2;
    delta: Vector2;
    /**
     * In pixels, how far the mouse moved since the previous event, also while the pointer is
     * locked, where its position stays the same.
     */
    movement?: Vector2;
  };
  wheel: { delta: number };
  button: { type: MouseButtonType };
  viewportPos?: {
    clientX: number;
    clientY: number;
  };
}

export interface KeyInputData {
  key: string;
  ctrl: boolean;
  metaKey: boolean;
  shift: boolean;
  alt: boolean;
  handled: boolean;
  e: KeyboardEvent;
}

export class InputEventDataExtractor {
  private previousToucheEventData: TouchInputData = {
    touches: [],
    changedTouches: [],
    center: undefined,
    touchLength: undefined,
  };
  private previousMouseEventData: MouseInputData | undefined;

  extractTouchesEventData(e: TouchEvent): TouchInputData {
    const newTouchInputData = this.extractToucheData(e);
    this.previousToucheEventData = newTouchInputData;
    return newTouchInputData;
  }

  extractMouseEventData(e: MouseEvent): MouseInputData {
    const newMouseInputData = this.extractMouseData(e);
    this.previousMouseEventData = newMouseInputData;
    return newMouseInputData;
  }

  extractKeyEventData(e: KeyboardEvent): KeyInputData {
    return {
      key: e.key,
      alt: e.altKey,
      ctrl: e.ctrlKey,
      metaKey: e.metaKey,
      shift: e.shiftKey,
      handled: false,
      e,
    };
  }

  private extractMouseData(e: MouseEvent): MouseInputData {
    const position = new Vector2(
      (e.offsetX / (e.target as HTMLElement).clientWidth) * 2 - 1,
      -(e.offsetY / (e.target as HTMLElement).clientHeight) * 2 + 1,
    );

    const delta = new Vector2();
    if (this.previousMouseEventData) {
      delta.x = this.previousMouseEventData.pointer.position.x - position.x;
      delta.y = this.previousMouseEventData.pointer.position.y - position.y;
    }

    let type: MouseEventType = MouseEventType.MouseMove;
    switch (e.type) {
      case 'mousemove':
        type = MouseEventType.MouseMove;
        break;
      case 'mouseup':
        type = MouseEventType.MouseUp;
        break;
      case 'mousedown':
        type = MouseEventType.MouseDown;
        break;
      case 'mousewheel':
        type = MouseEventType.MouseWheel;
        break;
    }

    let wheelDelta = 0;
    if (type === MouseEventType.MouseWheel) {
      wheelDelta = (e as any).wheelDelta;
    }

    return {
      type,
      pointer: {
        position,
        delta,
        movement: new Vector2(e.movementX, e.movementY),
        client: new Vector2(e.offsetX, e.offsetY),
      },
      button: { type: e.button },
      wheel: { delta: wheelDelta },
      viewportPos: {
        clientX: e.clientX,
        clientY: e.clientY,
      },
    };
  }

  private extractToucheData(e: TouchEvent): TouchInputData {
    const touchEventData: TouchInputData = {
      touches: [],
      changedTouches: [],
      center: undefined,
      touchLength: undefined,
    };

    if (e.touches.length > 2) {
      return touchEventData;
    }

    const oldTouchData = this.previousToucheEventData;
    _.range(e.touches.length).map((index) => {
      const position = new Vector2(
        (e.touches[index].pageX / window.innerWidth) * 2 - 1,
        -(e.touches[index].pageY / window.innerHeight) * 2 + 1,
      );

      const touchDelta = new Vector2();
      if (
        oldTouchData.touches[index] &&
        oldTouchData.touches[index].identifier === e.touches[index].identifier
      ) {
        touchDelta.x = position.x - oldTouchData.touches[index].position.x;
        touchDelta.y = position.y - oldTouchData.touches[index].position.y;
      }

      touchEventData.touches.push({
        position,
        delta: touchDelta,
        client: new Vector2(e.touches[index].clientX, e.touches[index].clientY),
        identifier: e.touches[index].identifier,
      });
    });

    _.range(e.changedTouches.length).map((index) => {
      const position = new Vector2(
        (e.changedTouches[index].pageX / window.innerWidth) * 2 - 1,
        -(e.changedTouches[index].pageY / window.innerHeight) * 2 + 1,
      );
      touchEventData.changedTouches.push({
        position,
        client: new Vector2(e.changedTouches[index].clientX, e.changedTouches[index].clientY),
      });
    });

    let centerPosition: Vector2 | undefined;

    if (touchEventData.touches.length === 1) {
      centerPosition = touchEventData.touches[0].position;
    } else if (touchEventData.touches.length > 1) {
      const touchDir = new Vector2().add(
        touchEventData.touches[0].position.clone().sub(touchEventData.touches[1].position),
      );

      centerPosition = touchEventData.touches[1].position
        .clone()
        .add(touchDir.clone().divideScalar(2));
      touchEventData.touchLength = { length: touchDir.length(), delta: 0 };
      if (oldTouchData.touches.length !== undefined && oldTouchData.touchLength !== undefined) {
        touchEventData.touchLength.delta =
          touchEventData.touchLength.length - oldTouchData.touchLength.length;
      }
    }

    if (centerPosition) {
      let touchDelta = new Vector2();
      if (oldTouchData && oldTouchData.center) {
        touchDelta = centerPosition.clone().sub(oldTouchData.center.position);
      }
      touchEventData.center = { position: centerPosition, delta: touchDelta };
    }

    return touchEventData;
  }
}
